import { t } from '../../../lib/i18n';
import { invoiceNumberLabel } from '../labels';
import type { FinanceTransaction } from '../types';
import {
  TRANSACTION_AMOUNT_TEXT,
  TRANSACTION_CHIP,
  cn,
  formatDayMonth,
  transactionAmount,
  transactionStatusKey,
} from '../utils';
import { PanelEmpty } from './panel-state';

/**
 * The desktop grid template, repeated by the header and every row.
 *
 * FIVE columns, not six. The artboard draws a «Спосіб» (payment method) column
 * and it is gone — see the note below.
 */
const COLUMNS = 'grid-cols-[90px_minmax(0,1.2fr)_minmax(0,1.8fr)_140px_140px] gap-3.5 px-[18px]';

/**
 * «Рух коштів і відкриті рахунки» — the money journal.
 *
 * ── The title is a correction, not a translation ────────────────────────────
 * The artboard calls this «Останні операції» and an operator reads that as a
 * money-event history: every euro that moved, in order. IT IS NOT ONE, and
 * shipping it under that title would be a lie they act on. `paid` and
 * `deposit_applied` are pure STATE FLIPS on a deal — they carry no amount, so
 * the journal has nothing to emit for them. The consequence is visible and
 * confusing: the `issued` row an operator saw yesterday DISAPPEARS once the
 * deal is settled, and no `in` row replaces it. Money did not vanish; the row
 * was a receivable, and the receivable is closed. So the table says what it
 * actually is — ledger movements plus still-open receivables — in both
 * catalogues.
 *
 * ── The «Спосіб» column is gone, and so is the filter over it ───────────────
 * `FinanceTransaction.rail` is `null` for every row this repo can produce, and
 * permanently so: `payments.schema.ts` has no rail column and the only
 * near-miss, `providerRef`, encodes a PURPOSE namespace
 * (`dealer_pro:sub:…`, `marketplace:consignment_fee:…`) rather than a rail.
 * A column of em dashes is furniture, and a filter over a field that can only
 * ever be empty is worse — it implies a fact the system does not hold. Both are
 * dropped. The column returns the day `payments` records a rail.
 *
 * Two shapes, as the artboards draw them: a grid that scrolls sideways below
 * 900px on desktop, and a stacked list on mobile where a multi-column grid
 * would be unreadable at any width. The stack is not a fallback — it carries
 * the same facts in reading order.
 *
 * The amount colour is the fastest read on the screen: green arrived, blue is
 * going back out to a client, ink is merely invoiced and has moved nothing.
 */
export function TransactionsTable({ transactions }: { transactions: FinanceTransaction[] }) {
  return (
    <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-1.5 leading-[normal] lg:overflow-x-auto lg:p-0">
      {/* ── Heading ─────────────────────────────────────────────────────── */}
      <div className="lg:flex lg:min-w-[900px] lg:items-center lg:justify-between lg:gap-2 lg:border-b lg:border-cf-line-soft lg:px-[18px] lg:py-3.5">
        <div className="mt-2.5 mb-1 lg:my-0">
          <h2 className="text-[16px] font-semibold lg:text-[17px] lg:tracking-[-0.02em]">
            {t('adminFinance.transactions.title')}
          </h2>
          <p className="mt-0.5 text-[12px] leading-[1.4] text-cf-ink-subtle">
            {t('adminFinance.transactions.subtitle')}
          </p>
        </div>
        {/*
          No journal route exists yet, so this is not a <Link> — and, per the
          same rule `finance-header.tsx` applies to «Експорт .xlsx», not a
          plain <span> either: a control that LOOKS like a link with no
          handler is a worse defect than a disabled one. `aria-disabled` +
          `title` + `cursor-not-allowed`, no native `disabled` (which would
          repaint it in the user agent's grey) and no `onClick`. It becomes a
          real <Link> the day /finance/journal does.
        */}
        <button
          type="button"
          aria-disabled
          title={t('adminFinance.transactions.fullUnavailable')}
          className="hidden cursor-not-allowed text-[13px] font-semibold text-cf-brand lg:inline"
        >
          {t('adminFinance.transactions.full')}
        </button>
      </div>

      {transactions.length === 0 ? (
        <div className="lg:px-[18px] lg:py-4">
          <PanelEmpty messageKey="adminFinance.transactions.empty" />
        </div>
      ) : (
        <>
          {/* ── Column header (desktop only) ────────────────────────────── */}
          <div
            className={cn(
              'hidden min-w-[900px] border-b border-cf-line-soft py-2.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:grid',
              COLUMNS,
            )}
          >
            <span>{t('adminFinance.transactions.date')}</span>
            <span>{t('adminFinance.transactions.client')}</span>
            <span>{t('adminFinance.transactions.operation')}</span>
            <span className="text-right">{t('adminFinance.transactions.sum')}</span>
            <span>{t('adminFinance.transactions.status')}</span>
          </div>

          {transactions.map((tx) => {
            const status = t(transactionStatusKey(tx.kind));
            const date = formatDayMonth(tx.at);
            const amount = transactionAmount(tx);
            // `reasonKey` is an i18n KEY the API chose, never a caption — a
            // rendered Ukrainian string would be undeliverable to an English
            // operator. The invoice it settles or raises rides beside it, with
            // a provisional number shown as what it is.
            const what = tx.invoiceNo
              ? `${t(tx.reasonKey)} · ${invoiceNumberLabel(tx.invoiceNo)}`
              : t(tx.reasonKey);
            return (
              <div key={tx.id}>
                {/* ── Desktop row ─────────────────────────────────────── */}
                <div
                  className={cn(
                    'hidden min-w-[900px] items-center border-b border-cf-line-soft py-[11px] text-[13px] lg:grid',
                    COLUMNS,
                  )}
                >
                  <span className="font-mono text-[12px] text-cf-ink-muted">{date}</span>
                  <span className="truncate font-semibold">{tx.clientName}</span>
                  <span className="truncate">{what}</span>
                  <span
                    className={cn(
                      'text-right font-mono font-medium',
                      TRANSACTION_AMOUNT_TEXT[tx.kind],
                    )}
                  >
                    {amount}
                  </span>
                  <span
                    className={cn(
                      'inline-flex w-fit rounded-cf-sm px-2 py-1 font-mono text-[10px] tracking-[0.08em] uppercase',
                      TRANSACTION_CHIP[tx.kind],
                    )}
                  >
                    {status}
                  </span>
                </div>

                {/* ── Mobile row ──────────────────────────────────────── */}
                <div className="flex items-start gap-2.5 border-t border-cf-line-soft py-2.5 text-[13px] lg:hidden">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold">{tx.clientName}</div>
                    <div className="truncate text-[12px] text-cf-ink-subtle">{what}</div>
                    <div
                      className={cn(
                        'mt-0.5 font-mono text-[10px]',
                        tx.kind === 'issued'
                          ? 'text-cf-ink-muted'
                          : TRANSACTION_AMOUNT_TEXT[tx.kind],
                      )}
                    >
                      {date} · {status}
                    </div>
                  </div>
                  <span
                    className={cn(
                      'font-mono font-medium whitespace-nowrap',
                      TRANSACTION_AMOUNT_TEXT[tx.kind],
                    )}
                  >
                    {amount}
                  </span>
                </div>
              </div>
            );
          })}
        </>
      )}
    </section>
  );
}
